import React, { useContext, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import UploadPrescriptionSlider from "./UploadPrescriptionSlider";
import CallPopup from "../../common/CallPopup.js";
import { usePopupLock } from "../../../context/PopupLockContext";
import { Upload, Phone, FileText, Headphones, ShieldCheck, CheckCircle2 } from "lucide-react";
import { FaShieldAlt, FaClock, FaAward, FaHeart } from "react-icons/fa";

export default function PrescriptionOptions({ setShowLogin }) {
  const { user } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  const [showUpload, setShowUpload] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);

  const isLoggedIn = !!user;

  const handleUploadClick = () => {
    if (!isLoggedIn) return setShowLogin?.(true);
    setShowUpload(true);
    openLock("call");
  };

  const handleCallClick = () => {
    setShowCallPopup(true);
    openLock("call");
  };

  return (
    <>
      <div className="px-4 md:px-8 py-7 bg-white rounded-3xl border border-slate-200/80 shadow-xs mt-2">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 pb-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-red-600">Quick Assistance</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
              Order with <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">Doctor's Prescription</span>
            </h2>
            <p className="text-slate-500 text-xs md:text-sm mt-1">
              Upload prescription or consult clinical pharmacists to prepare your test & medicine order
            </p>
          </div>
          <div className="hidden md:flex items-center gap-4 text-xs font-semibold text-slate-600">
            <span className="flex items-center gap-1.5 text-emerald-600"><CheckCircle2 size={15} /> 100% Confidential</span>
            <span className="flex items-center gap-1.5 text-emerald-600"><CheckCircle2 size={15} /> Certified Pharmacists</span>
          </div>
        </div>

        {/* CARDS - Apollo Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          
          {/* UPLOAD CARD */}
          <div className="group relative bg-gradient-to-br from-red-50/40 via-white to-slate-50 rounded-2xl p-5 md:p-6 border border-red-100/90 shadow-2xs hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-start gap-4">
              <div className="w-13 h-13 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center text-white shadow-sm shadow-red-200 flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                <Upload size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-black text-slate-900 group-hover:text-red-600 transition-colors">
                    Upload Prescription
                  </h3>
                  <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    Fast Track
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Upload your doctor's slip & let our licensed clinical pharmacists curate and book your tests.
                </p>
                
                <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-slate-600 font-medium">
                  <span className="flex items-center gap-1 text-emerald-600 font-bold">
                    <span>✓</span> Free Sample Pickup
                  </span>
                  <span className="flex items-center gap-1 text-emerald-600 font-bold">
                    <span>✓</span> Best Price Guarantee
                  </span>
                </div>

                <button
                  onClick={handleUploadClick}
                  className="mt-4 w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl text-xs font-bold hover:from-red-700 hover:to-rose-700 transition-all duration-200 shadow-sm shadow-red-200 hover:shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FileText size={15} />
                  <span>Upload & Order Now</span>
                </button>
              </div>
            </div>
          </div>

          {/* CALL CARD */}
          <div className="group relative bg-gradient-to-br from-blue-50/40 via-white to-slate-50 rounded-2xl p-5 md:p-6 border border-blue-100/90 shadow-2xs hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-start gap-4">
              <div className="w-13 h-13 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-sm shadow-blue-200 flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                <Phone size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                    Consult Health Expert
                  </h3>
                  <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Online Now
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Need help selecting tests or blood panels? Connect with medical advisors for instant guidance.
                </p>
                
                <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-slate-600 font-medium">
                  <span className="flex items-center gap-1 text-emerald-600 font-bold">
                    <span>✓</span> Zero Advisory Fee
                  </span>
                  <span className="flex items-center gap-1 text-emerald-600 font-bold">
                    <span>✓</span> Instant Callback
                  </span>
                </div>

                <button
                  onClick={handleCallClick}
                  className="mt-4 w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-xs font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm shadow-blue-200 hover:shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Headphones size={15} />
                  <span>Call Health Expert</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-6 pt-4 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-50 rounded-xl p-2 border border-slate-100">
              <span className="text-emerald-600 font-bold">✓</span>
              <span>100% Genuine Medicine</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-50 rounded-xl p-2 border border-slate-100">
              <span className="text-blue-600 font-bold">⚡</span>
              <span>Express 2-Hr Delivery</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-50 rounded-xl p-2 border border-slate-100">
              <span className="text-purple-600 font-bold">🛡️</span>
              <span>Encrypted Data Privacy</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-50 rounded-xl p-2 border border-slate-100">
              <span className="text-red-600 font-bold">❤️</span>
              <span>Trusted by 10L+ Users</span>
            </div>
          </div>
        </div>
      </div>

      {/* POPUPS */}
      {showUpload && (
        <UploadPrescriptionSlider
          isOpen={showUpload}
          onClose={() => {
            setShowUpload(false);
            closeLock("call");
          }}
        />
      )}

      {showCallPopup && (
        <CallPopup
          isOpen={showCallPopup}
          onClose={() => {
            setShowCallPopup(false);
            closeLock("call");
          }}
          user={user}
        />
      )}
    </>
  );
}